import type {
  NameType,
  Payload,
  ValueType
} from "recharts/types/component/DefaultTooltipContent";
import { twMerge } from "tailwind-merge";

export type ChartTooltipPayload = Payload<ValueType, NameType>;

export interface ChartTooltipProps {
  active?: boolean;
  payload?: ChartTooltipPayload[];
  totalValue?: number;
  /**
   * Function to extract the label from a payload item.
   * Defaults to `item.name`
   */
  getLabelFromItem?: (item: ChartTooltipPayload) => string;
  /**
   * Function to format the percentage value.
   * Defaults to `toLocaleString()`
   */
  formatPercentage?: (percent: number) => string;
}

/**
 * Reusable chart tooltip component for bar charts.
 * Displays payload items with their values and percentages.
 */
export const ChartTooltip = ({
  active,
  payload,
  totalValue,
  getLabelFromItem = (item: ChartTooltipPayload): string => {
    const name = item.name;
    return name === undefined ? "" : String(name);
  },
  formatPercentage = (percent: number): string => percent.toLocaleString()
}: ChartTooltipProps) => {
  if (!active || !payload?.length) return null;

  return (
    <div
      className={twMerge(
        "flex flex-col gap-2 bg-white border border-whisper rounded-[10px] p-3",
        "shadow-[0_2px_8px_rgba(0,0,0,0.08)] min-w-[170px]"
      )}
    >
      {payload.map((item) => {
        const numValue = Number(item.value ?? 0);
        const percentValue = totalValue ? (numValue / totalValue) * 100 : 0;
        const label = getLabelFromItem(item);
        const formattedPercent = formatPercentage(percentValue);

        return (
          <div key={label}>
            <span className="text-xs text-txtHeading font-normal">{label}</span>
            <span className="text-xs text-txtHeading font-semibold">
              {`: ${numValue === 0 ? "null" : numValue.toLocaleString()} (${formattedPercent}%)`}
            </span>
          </div>
        );
      })}
    </div>
  );
};
